import React from 'react';

/**
 * AttendanceClientSkeleton component
 *
 * This component provides a loading skeleton for the AttendanceClient page.
 * It includes placeholders for the header, date display, student list, and action buttons.
 */
export const AttendanceClientSkeleton = () => {
  return (
    <div className="h-[calc(100vh-var(--top-navbar-height,52px))] flex flex-col overflow-hidden bg-white animate-pulse">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pt-6 md:max-w-[1200px] md:mx-auto md:p-6">
          {/* Header Section */}
          <div className="space-y-3 md:space-y-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 w-9 h-9 bg-gray-100 rounded-xl"></div>
              <div>
                <div className="h-7 w-36 bg-gray-200 rounded-lg mb-2"></div>
                <div className="h-4 w-48 bg-gray-100 rounded"></div>
              </div>
            </div>

            {/* Date Display */}
            <div className="space-y-2">
              <div className="bg-[#F5F8F9] rounded-xl p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-gray-200 rounded"></div>
                  <div className="h-5 w-40 bg-gray-200 rounded"></div>
                </div>
              </div>

              {/* Mark All Present Button */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 rounded-xl transition-colors text-sm flex-1">
                  <div className="w-4 h-4 rounded bg-gray-200"></div>
                  <div className="h-4 w-48 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Student List - Desktop */}
          <div className="hidden md:block mt-3">
            <div className="bg-white rounded-xl border border-[#E4E8EA] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E4E8EA] bg-white">
                    <th className="w-[5.5rem] py-3 pl-4 pr-2 text-left">
                      <div className="h-4 w-16 bg-gray-200 rounded"></div>
                    </th>
                    <th className="py-3 pl-2 pr-4 text-left">
                      <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    </th>
                    <th className="py-3 px-4 text-right w-[120px]">
                      <div className="h-4 w-20 bg-gray-200 rounded float-right"></div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array(8)
                    .fill(0)
                    .map((_, index) => (
                      <tr
                        key={index}
                        className="border-b border-[#E4E8EA] last:border-0"
                      >
                        <td className="w-[5.5rem] py-3 pl-4 pr-2 align-middle">
                          <div className="h-4 w-12 bg-gray-200 rounded"></div>
                        </td>
                        <td className="py-3 pl-2 pr-4 align-middle">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gray-200"></div>
                            <div className="h-4 w-32 bg-gray-200 rounded"></div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right align-middle">
                          <div className="flex justify-end gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gray-200"></div>
                            <div className="w-8 h-8 rounded-lg bg-gray-200"></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Student List - Mobile */}
          <div className="md:hidden -mx-4 mt-3">
            <div className="bg-white border-y border-[#E4E8EA] px-2">
              {/* Header */}
              <div className="grid grid-cols-[4.5rem,1fr] border-b border-[#E4E8EA]">
                <div className="p-3">
                  <div className="h-3 w-12 bg-gray-200 rounded"></div>
                </div>
                <div className="p-3">
                  <div className="h-3 w-16 bg-gray-200 rounded"></div>
                </div>
              </div>

              {/* Student List */}
              <div className="divide-y divide-[#E4E8EA]">
                {Array(5)
                  .fill(0)
                  .map((_, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[4.5rem,1fr] relative overflow-hidden"
                    >
                      {/* Roll Number */}
                      <div className="p-3 bg-white">
                        <div className="h-3 w-10 bg-gray-200 rounded"></div>
                      </div>

                      {/* Student Info */}
                      <div className="p-3 bg-white">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gray-200"></div>
                          <div>
                            <div className="h-3 w-24 bg-gray-200 rounded"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Save Button */}
      <div className="mx-4 mb-4 max-[900px]:mb-20 bg-white border-t border-[#E4E8EA] shrink-0 pt-4">
        <div className="md:max-w-[600px] md:mx-auto">
          <div className="w-full px-4 py-3 rounded-xl bg-gray-200 h-10"></div>
        </div>
      </div>
    </div>
  );
};
